import React, { useState } from 'react';
import { Send, Upload, X, Check, Car } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FitmentCheck({ productSlug, productName }) {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    message: '',
    customer_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    customer_email: user?.email || '',
    customer_phone: '',
    vehicle_info: '',
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles].slice(0, 5));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_email) { setError('Bitte geben Sie Ihre E-Mail-Adresse an'); return; }
    if (!form.message && !form.vehicle_info) { setError('Bitte beschreiben Sie Ihre Anfrage'); return; }

    setSubmitting(true);
    setError('');

    try {
      // Create fitment request
      const formData = new FormData();
      formData.append('product_slug', productSlug);
      formData.append('product_name', productName || '');
      formData.append('message', form.message);
      formData.append('customer_name', form.customer_name);
      formData.append('customer_email', form.customer_email);
      formData.append('customer_phone', form.customer_phone);
      formData.append('vehicle_info', form.vehicle_info);

      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API}/api/fitment/check`, {
        method: 'POST',
        headers,
        body: formData,
      });
      const data = await res.json();

      if (res.ok && data.request_id) {
        // Upload files if any
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          await fetch(`${API}/api/fitment/check/${data.request_id}/upload`, {
            method: 'POST',
            body: fd,
          });
        }
        setSubmitted(true);
      } else {
        setError(data.detail || 'Fehler beim Senden');
      }
    } catch {
      setError('Verbindungsfehler');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="fitment-section" data-testid="fitment-check">
        <div className="fitment-success" data-testid="fitment-success">
          <Check size={24} />
          <h3>Passform-Anfrage gesendet!</h3>
          <p>Wir prüfen die Kompatibilität und melden uns innerhalb von 24 Stunden bei Ihnen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fitment-section" data-testid="fitment-check">
      {!open ? (
        <button className="btn-fitment-check" data-testid="open-fitment-btn" onClick={() => setOpen(true)}>
          <Car size={18} /> Passform-Check: Passt das zu meinem Auto?
        </button>
      ) : (
        <div className="fitment-form-wrapper" data-testid="fitment-form">
          <div className="fitment-header">
            <h3><Car size={18} /> Passform-Check</h3>
            <button className="fitment-close" onClick={() => setOpen(false)}><X size={18} /></button>
          </div>
          <p className="fitment-desc">Senden Sie uns Ihren Fahrzeugschein + Fotos vom Auto. Wir prüfen die Kompatibilität kostenlos!</p>

          {error && <div className="fitment-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="fitment-fields">
              <div className="form-group">
                <label>Ihr Fahrzeug</label>
                <input type="text" value={form.vehicle_info} onChange={e => setForm(f => ({ ...f, vehicle_info: e.target.value }))}
                  placeholder="z.B. VW Golf 7 GTI, 2018, 2.0 TSI" data-testid="fitment-vehicle" />
              </div>
              <div className="form-group">
                <label>Ihre Nachricht</label>
                <textarea rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Beschreiben Sie Ihre Frage zur Passform..." data-testid="fitment-message" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="fitment-name" />
                </div>
                <div className="form-group">
                  <label>E-Mail *</label>
                  <input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                    required data-testid="fitment-email" />
                </div>
              </div>
              <div className="form-group">
                <label>Telefon (optional)</label>
                <input type="tel" value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} data-testid="fitment-phone" />
              </div>

              {/* File Upload */}
              <div className="form-group">
                <label>Fahrzeugschein & Fotos hochladen (max. 5)</label>
                <div className="file-upload-area" data-testid="fitment-upload">
                  <input type="file" accept="image/*,.pdf" multiple onChange={handleFileChange} id="fitment-files" style={{ display: 'none' }} />
                  <label htmlFor="fitment-files" className="file-upload-btn">
                    <Upload size={16} /> Dateien auswählen
                  </label>
                  {files.length > 0 && (
                    <div className="file-list">
                      {files.map((f, i) => (
                        <div className="file-item" key={i}>
                          <span>{f.name}</span>
                          <button type="button" onClick={() => removeFile(i)}><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" className="btn-submit-fitment" disabled={submitting} data-testid="submit-fitment-btn">
              <Send size={14} /> {submitting ? 'Wird gesendet...' : 'Passform-Anfrage senden'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
